import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();

  const token = searchParams.get('token');

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        setError('No verification token provided');
        return;
      }

      try {
        await verifyEmail(token);
        setStatus('success');
        toast.success('Email verified successfully!');
      } catch (err) {
        setStatus('error');
        const message = err.response?.data?.detail || 'Verification failed';
        setError(message);
        toast.error(message);
      }
    };

    verify();
  }, [token, verifyEmail]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg border-slate-100 animate-slideUp">
        <CardHeader className="text-center">
          {status === 'verifying' && (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
              </div>
              <CardTitle className="text-2xl font-heading">Verifying your email</CardTitle>
              <CardDescription>Please wait while we verify your email address...</CardDescription>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-teal-100 flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-teal-600" />
              </div>
              <CardTitle className="text-2xl font-heading">Email verified!</CardTitle>
              <CardDescription>
                Your email has been verified successfully. You can now sign in and start building your career profile.
              </CardDescription>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <CardTitle className="text-2xl font-heading">Verification failed</CardTitle>
              <CardDescription className="text-red-600">{error}</CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'success' && (
            <Button
              className="w-full h-11 bg-teal-600 hover:bg-teal-700"
              onClick={() => navigate('/login')}
              data-testid="verified-login-btn"
            >
              Sign In Now
            </Button>
          )}

          {status === 'error' && (
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full h-11"
                onClick={() => navigate('/login')}
                data-testid="error-login-btn"
              >
                Try signing in
              </Button>
              <p className="text-sm text-slate-500 text-center">
                If you need a new verification link, please{' '}
                <Link to="/register" className="text-teal-600 hover:text-teal-700 font-medium">
                  register again
                </Link>
                {' '}or contact support.
              </p>
            </div>
          )}

          <Link 
            to="/" 
            className="block text-center text-sm text-slate-500 hover:text-slate-700"
          >
            Back to home
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}

// Pending verification page (shown after registration)
export function VerifyPendingPage() {
  const [resending, setResending] = useState(false);
  const { user, resendVerification, logout } = useAuth();

  const handleResend = async () => {
    if (!user?.email) return;
    
    setResending(true);
    try {
      await resendVerification(user.email);
      toast.success('Verification email sent!');
    } catch (err) {
      toast.error('Failed to send verification email');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg border-slate-100 animate-slideUp">
        <CardHeader className="text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-teal-100 flex items-center justify-center mb-4">
            <Mail className="h-8 w-8 text-teal-600" />
          </div>
          <CardTitle className="text-2xl font-heading">Verify your email</CardTitle>
          <CardDescription>
            We've sent a verification link to{' '}
            <span className="font-medium text-slate-900">{user?.email}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500 text-center">
            Click the link in your email to verify your account. Can't find it? Check your spam folder.
          </p>
          
          <Button
            variant="outline"
            className="w-full h-11"
            onClick={handleResend}
            disabled={resending}
            data-testid="resend-verification-btn"
          >
            {resending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Resend verification email'
            )}
          </Button>

          <Button
            variant="ghost"
            className="w-full"
            onClick={logout}
            data-testid="logout-btn"
          >
            Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
